import 'client-only'
import {SessionServiceInterface} from '@/src/core/domain/session'
import type {StorageAdapter} from '@/src/core/domain/data/storage'
import {Routes} from '@/src/config/routes'
import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'

import type {IApiClientServiceFactory} from '@/src/ports/services/api'
import type {QueryCacheInterface} from '@/src/ports/services/query-cache'
import type {ClientSession} from '@/src/core/domain/session/client-session'
import {getClientSessionLifecycle} from './client-session-lifecycle'

@injectable()
export class MobileSessionService implements SessionServiceInterface {
  @inject(DI.StorageAdapter)
  storage!: StorageAdapter

  @inject(DI.IApiClientServiceFactory)
  apiClientFactory!: IApiClientServiceFactory

  @inject(DI.IQueryCacheService)
  queryCache!: QueryCacheInterface

  async load(): Promise<[Nullable<ClientSession>, Record<any, any>]> {
    const lifecycle = getClientSessionLifecycle()
    const generation = lifecycle?.capture()
    const storageItem = await this.storage.getItem<{value: string} | string>('session')
    if (lifecycle && !lifecycle.isCurrent(generation!)) return [null, {}]
    let mobileSessionString: Nullable<string> = null

    if (storageItem) {
      mobileSessionString = typeof storageItem === 'string' ? storageItem : storageItem.value
    }

    if (!mobileSessionString) {
      return [null, {}]
    }

    const session = this.queryCache.getQueryData<Nullable<ClientSession>>(['session'])
    const cachedSession = session !== undefined
      ? session
      : this.queryCache.getQueryData<LayoutData>(['layout'])?.session ?? null

    return [cachedSession, await this.getAuthHeaders(mobileSessionString)]
  }

  async refresh(): Promise<Nullable<ClientSession>> {
    const storageItem = await this.storage.getItem<{value: string} | string>('session')
    const mobileSessionString =
      typeof storageItem === 'string' ? storageItem : (storageItem?.value ?? null)

    if (mobileSessionString) {
      const client = this.apiClientFactory('api')
      return await client.get<Nullable<ClientSession>>(Routes.api.auth.refreshSession, {
        credentials: 'include',
        headers: {'X-Auth': mobileSessionString},
      })
    }
    return null
  }

  async clear() {
    const lifecycle = getClientSessionLifecycle()
    lifecycle?.terminate()
    this.queryCache.setQueryData(['session'], null)
    const layout = this.queryCache.getQueryData<LayoutData>(['layout'])
    if (layout) {
      const header = {...layout.header}
      delete header.cart
      this.queryCache.setQueryData(['layout'], {...layout, header, session: null})
    }
    const clearStorage = async () => {
      await this.storage.removeItem('session')
      await this.storage.removeItem('mobileCsrf')
    }
    if (lifecycle) await lifecycle.mutateStorage(clearStorage)
    else await clearStorage()
  }

  async getAuthHeaders(sessionString: string | null) {
    const authHeaders: Record<any, any> = {}

    if (sessionString) {
      authHeaders['X-Auth'] = sessionString
    }

    return authHeaders
  }
}
